import { trainingRequestKey } from "../../../api/training";
import { sanitizeRichHtml } from "../../utils/sanitize-rich-html";
import { missingTranslations, readTranslation, translationFields, writeTranslation } from "./trainingTranslations";

export const TRAINING_JSON_SCHEMA = "promang.training-guide.content.v1";
export const TRAINING_JSON_LOCALES = ["nb", "en", "sv", "pl"];
const MAX_INPUT_BYTES = 250000;
const MAX_CONTENT_BYTES = 200000;
const TYPES = [
  "content",
  "acknowledgement",
  "single_choice",
  "multiple_choice",
  "document",
];
const CARD_FIELDS = [
  "key",
  "type",
  "title",
  "body",
  "required",
  "image_document_id",
  "image_alt",
  "options",
  "correct_option_ids",
  "explanation",
  "document_version_id",
  "document_required",
  "depends_on",
];
const SAFE_TAGS = new Set(
  "p br strong b em i u s ul ol li blockquote h2 h3 h4 a code pre table tbody tr th td".split(
    " ",
  ),
);
const fail = (code) => {
  const error = new Error(code);
  error.code = code;
  throw error;
};
const bytes = (value) => new Blob([value]).size;
const object = (value) =>
  value && typeof value === "object" && !Array.isArray(value);
function fields(value, allowed) {
  if (!object(value)) fail("invalid");
  if (Object.keys(value).some((key) => !allowed.includes(key)))
    fail("unsupported");
}
function string(value, max, fallback = "") {
  if (value === undefined) return fallback;
  if (typeof value !== "string") fail("invalid");
  if (value.length > max) fail("size");
  return value;
}
function key(value) {
  const result = value === undefined ? trainingRequestKey() : value;
  if (typeof result !== "string" || !/^[a-zA-Z0-9_-]{1,100}$/.test(result))
    fail("identity");
  return result;
}
function bool(value, fallback) {
  if (value === undefined) return fallback;
  if (typeof value !== "boolean") fail("invalid");
  return value;
}
function reference(value) {
  if (value == null) return null;
  if (typeof value !== "number" && typeof value !== "string") fail("reference");
  if (!/^[1-9]\d*$/.test(String(value)) || !Number.isSafeInteger(Number(value)))
    fail("reference");
  return Number(value);
}
function list(value, max) {
  if (value === undefined) return [];
  if (!Array.isArray(value)) fail("invalid");
  if (value.length > max) fail("size");
  return value;
}
function unique(values) {
  if (new Set(values).size !== values.length) fail("identity");
  return values;
}

// Same shared sanitizer as the player, followed by the backend Content safe list.
// Imported HTML never loads external images; media uses authorized document IDs.
export function sanitizeTrainingImportHtml(value) {
  const template = document.createElement("template");
  template.innerHTML = sanitizeRichHtml(string(value, MAX_CONTENT_BYTES));
  [...template.content.querySelectorAll("*")].reverse().forEach((element) => {
    if (!SAFE_TAGS.has(element.tagName.toLowerCase())) {
      element.replaceWith(...element.childNodes);
      return;
    }
    [...element.attributes].forEach((attribute) => {
      if (!["href", "title"].includes(attribute.name))
        element.removeAttribute(attribute.name);
    });
  });
  return template.innerHTML.trim();
}

function explanationText(value) {
  const template = document.createElement("template");
  template.innerHTML = sanitizeTrainingImportHtml(value);
  template.content
    .querySelectorAll("br")
    .forEach((element) => element.replaceWith("\n"));
  template.content
    .querySelectorAll("p,li,h2,h3,h4,blockquote,pre,tr")
    .forEach((element) => element.append("\n"));
  return template.content.textContent.trim();
}

function normalizeCard(raw) {
  fields(raw, CARD_FIELDS);
  if (!TYPES.includes(raw.type)) fail("unsupported");
  const options = list(raw.options, 20).map((option) => {
    fields(option, ["id", "text"]);
    return { id: key(option.id), text: string(option.text, 2000).trim() };
  });
  const ids = unique(options.map((option) => option.id));
  const correct = unique(list(raw.correct_option_ids, 20).map(key));
  if (correct.some((id) => !ids.includes(id))) fail("answers");
  const card = {
    key: key(raw.key),
    type: raw.type,
    title: string(raw.title, 200).trim(),
    body: sanitizeTrainingImportHtml(raw.body),
    required: bool(raw.required, true),
    image_document_id: reference(raw.image_document_id),
    image_alt: string(raw.image_alt, 500),
    options,
    correct_option_ids: correct,
    explanation: explanationText(raw.explanation),
    document_version_id: reference(raw.document_version_id),
    document_required: bool(raw.document_required, raw.type === "document"),
    depends_on: unique(list(raw.depends_on, 200).map(key)),
  };
  if (!card.title && !card.body) fail("invalid");
  if (
    ["single_choice", "multiple_choice"].includes(card.type) &&
    (options.length < 2 ||
      options.some((option) => !option.text) ||
      !correct.length ||
      (card.type === "single_choice" && correct.length !== 1))
  )
    fail("answers");
  if (
    (card.type === "document" || card.document_required) &&
    !card.document_version_id
  )
    fail("reference");
  if (card.image_document_id && !card.image_alt.trim()) fail("reference");
  return card;
}

function validatePages(pages) {
  if (!pages.length) fail("invalid");
  if (pages.length > 50 || bytes(JSON.stringify(pages)) > MAX_CONTENT_BYTES)
    fail("size");
  unique(pages.map((page) => page.key));
  const cards = pages.flatMap((page) => page.cards);
  if (!cards.length) fail("invalid");
  if (cards.length > 200) fail("size");
  unique(cards.map((card) => card.key));
  const byKey = new Map(cards.map((card) => [card.key, card]));
  const visiting = new Set();
  const visited = new Set();
  const visit = (id) => {
    if (visited.has(id)) return;
    if (!byKey.has(id) || visiting.has(id)) fail("dependency");
    visiting.add(id);
    byKey.get(id).depends_on.forEach(visit);
    visiting.delete(id);
    visited.add(id);
  };
  cards.forEach((card) => visit(card.key));
}

function normalizeAudience(raw) {
  fields(raw, ["type", "position_ids"]);
  if (!["all", "positions"].includes(raw.type)) fail("audience");
  const ids = list(raw.position_ids, 2000);
  if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0) || new Set(ids).size !== ids.length)
    fail("audience");
  if ((raw.type === "positions" && !ids.length) || (raw.type === "all" && ids.length)) fail("audience");
  return { type: raw.type, position_ids: ids };
}

function locale(value) {
  if (!TRAINING_JSON_LOCALES.includes(value)) fail("language");
  return value;
}

// Text-only overlays keyed by the imported page, card and option keys (see API Training::Translations).
function normalizeTranslations(raw, pages, base) {
  if (!object(raw)) fail("invalid");
  const pageKeys = new Set(pages.map((page) => page.key));
  const cards = new Map(pages.flatMap((page) => page.cards).map((card) => [card.key, card]));
  return Object.fromEntries(
    Object.entries(raw).map(([code, overlay]) => {
      if (locale(code) === base) fail("language");
      fields(overlay, ["title", "description", "pages", "cards"]);
      const result = { pages: {}, cards: {} };
      if (overlay.title !== undefined) result.title = string(overlay.title, 200).trim();
      if (overlay.description !== undefined) result.description = string(overlay.description, 4000);
      Object.entries(overlay.pages || {}).forEach(([pageKey, page]) => {
        if (!pageKeys.has(pageKey)) fail("translations");
        fields(page, ["title"]);
        result.pages[pageKey] = { title: string(page.title, 200).trim() };
      });
      if (overlay.pages !== undefined && !object(overlay.pages)) fail("invalid");
      if (overlay.cards !== undefined && !object(overlay.cards)) fail("invalid");
      Object.entries(overlay.cards || {}).forEach(([cardKey, text]) => {
        const card = cards.get(cardKey);
        if (!card) fail("translations");
        fields(text, ["title", "body", "explanation", "image_alt", "options"]);
        const row = {};
        if (text.title !== undefined) row.title = string(text.title, 200).trim();
        if (text.body !== undefined) row.body = sanitizeTrainingImportHtml(text.body);
        if (text.explanation !== undefined) row.explanation = explanationText(text.explanation);
        if (text.image_alt !== undefined) row.image_alt = string(text.image_alt, 500);
        if (text.options !== undefined) {
          if (!object(text.options)) fail("invalid");
          const ids = new Set(card.options.map((option) => option.id));
          row.options = Object.fromEntries(
            Object.entries(text.options).map(([id, value]) => {
              if (!ids.has(id)) fail("translations");
              return [id, string(value, 2000).trim()];
            }),
          );
        }
        result.cards[cardKey] = row;
      });
      return [code, result];
    }),
  );
}

export function validateTrainingAudience(content, positions) {
  if (!content.audience) return;
  if (!Array.isArray(positions)) fail("positionsUnavailable");
  const allowed = new Set(positions.map((position) => Number(position.id)));
  if (content.audience.position_ids.some((id) => !allowed.has(id))) fail("audience");
}

/**
 * Every bound document must be a current approved company HMS document version from the
 * catalogue (the AI instructions list them). Skipped while the catalogue is not loaded.
 */
export function validateTrainingDocuments(content, documents) {
  const cards = (content?.pages || []).flatMap((page) => page.cards || []);
  const linked = cards.some((card) => card.document_version_id != null);
  if (!Array.isArray(documents)) {
    // Fail closed: without the catalogue we cannot tell whether a version is allowed.
    if (linked) fail("documentsUnavailable");
    return;
  }
  const allowed = new Set(documents.map((doc) => Number(doc.document_version_id)));
  if (cards.some((card) => card.document_version_id != null && !allowed.has(Number(card.document_version_id)))) {
    fail("documentUnknown");
  }
}

export function parseTrainingGuideJson(text) {
  if (typeof text !== "string" || !text.trim()) fail("invalid");
  if (bytes(text) > MAX_INPUT_BYTES) fail("size");
  const trimmed = text
    .trim()
    .replace(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/i, "$1");
  let raw;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    fail("invalid");
  }
  fields(raw, ["schema", "title", "description", "pages", "audience", "content_locale", "translations"]);
  if (raw.schema !== undefined && raw.schema !== TRAINING_JSON_SCHEMA)
    fail("unsupported");
  const pages = list(raw.pages, 50).map((page) => {
    fields(page, ["key", "title", "cards"]);
    return {
      key: key(page.key),
      title: string(page.title, 200).trim(),
      cards: list(page.cards, 200).map(normalizeCard),
    };
  });
  validatePages(pages);
  const contentLocale = raw.content_locale === undefined ? undefined : locale(raw.content_locale);
  return {
    ...(contentLocale ? { content_locale: contentLocale } : {}),
    ...(raw.translations !== undefined
      ? { translations: normalizeTranslations(raw.translations, pages, contentLocale || "nb") }
      : {}),
    ...(raw.audience !== undefined ? { audience: normalizeAudience(raw.audience) } : {}),
    ...(raw.title !== undefined
      ? { title: string(raw.title, 200).trim() }
      : {}),
    ...(raw.description !== undefined
      ? { description: string(raw.description, 4000) }
      : {}),
    pages,
  };
}

/** Only content and supported audience fields; never user identities or permissions. */
export function serializeTrainingGuideJson(draft) {
  return JSON.stringify(
    {
      schema: TRAINING_JSON_SCHEMA,
      content_locale: draft.revision.content_locale || "nb",
      title: draft.guide.title || "",
      description: draft.guide.description || "",
      ...(["all", "positions"].includes(draft.revision.policy?.audience) ? {
        audience: { type: draft.revision.policy.audience, position_ids: draft.revision.policy.audience === "positions" ? (draft.revision.policy.position_ids || []) : [] },
      } : {}),
      pages: draft.revision.pages.map((page) => ({
        key: page.key,
        title: page.title || "",
        cards: page.cards.map((card) =>
          Object.fromEntries(
            CARD_FIELDS.filter((field) => card[field] !== undefined).map(
              (field) => [
                field,
                field === "options"
                  ? card.options.map(({ id, text }) => ({ id, text }))
                  : card[field],
              ],
            ),
          ),
        ),
      })),
      translations: draft.revision.translations || {},
    },
    null,
    2,
  );
}

// Appended text becomes course text, so it must be in the course language. JSON written in
// another language is switched over when it carries a complete translation into it.
function inCourseLanguage(content, base) {
  if (!content.content_locale && !content.translations) return content;
  const from = content.content_locale || "nb";
  if (from === base) return content;
  const target = content.translations?.[base];
  const source = { guide: {}, revision: { pages: content.pages } };
  if (!target || missingTranslations(source, target).length) fail("languageMismatch");
  const pages = JSON.parse(JSON.stringify(content.pages));
  let original = { pages: {}, cards: {} };
  translationFields(source).forEach((field) => {
    original = writeTranslation(original, field, field.base);
    const text = readTranslation(target, field);
    const page = pages[field.pageIndex];
    if (field.kind === "page") page.title = text;
    else {
      const card = page.cards.find((row) => row.key === field.cardKey);
      if (field.kind === "card") card[field.field] = text;
      else card.options[field.optionIndex].text = text;
    }
  });
  const { [base]: _target, ...rest } = content.translations;
  return { ...content, content_locale: base, pages, translations: { ...rest, [from]: original } };
}

export function applyTrainingGuideContent(
  draft,
  content,
  mode,
  generateKey = trainingRequestKey,
) {
  if (!["replace", "append"].includes(mode)) fail("unsupported");
  const base = draft.revision.content_locale || "nb";
  if (mode === "append") content = inCourseLanguage(content, base);
  let pages = content.pages;
  let translations = content.translations;
  // A new course language cannot keep a translation into itself.
  if (mode === "replace" && !translations && content.content_locale) {
    const { [content.content_locale]: _dropped, ...rest } = draft.revision.translations || {};
    translations = rest;
  }
  if (mode === "append") {
    const used = new Set(
      [...draft.revision.pages, ...pages].flatMap((page) => [
        page.key,
        ...page.cards.flatMap((card) => [
          card.key,
          ...(card.options || []).map((option) => option.id),
        ]),
      ]),
    );
    const fresh = () => {
      for (let tries = 0; tries < 100; tries += 1) {
        const candidate = key(generateKey());
        if (!used.has(candidate)) {
          used.add(candidate);
          return candidate;
        }
      }
      return fail("identity");
    };
    const cardKeys = new Map(
      pages.flatMap((page) => page.cards.map((card) => [card.key, fresh()])),
    );
    const pageKeys = new Map(pages.map((page) => [page.key, fresh()]));
    const optionMaps = new Map(
      pages.flatMap((page) => page.cards).map((card) => [card.key, new Map(card.options.map((option) => [option.id, fresh()]))]),
    );
    // Imported translations follow the new identities and are merged into existing languages.
    if (translations) {
      const existing = draft.revision.translations || {};
      translations = Object.fromEntries(
        Object.entries({ ...Object.fromEntries(Object.keys(existing).map((code) => [code, {}])), ...translations }).map(([code, overlay]) => {
          const current = existing[code] || {};
          return [code, {
            ...current,
            pages: { ...(current.pages || {}), ...Object.fromEntries(Object.entries(overlay.pages || {}).map(([pageKey, value]) => [pageKeys.get(pageKey), value])) },
            cards: { ...(current.cards || {}), ...Object.fromEntries(Object.entries(overlay.cards || {}).map(([cardKey, value]) => [cardKeys.get(cardKey), value.options ? { ...value, options: Object.fromEntries(Object.entries(value.options).map(([id, text]) => [optionMaps.get(cardKey).get(id), text])) } : value])) },
          }];
        }),
      );
    }
    pages = pages.map((page) => ({
      ...page,
      key: pageKeys.get(page.key),
      cards: page.cards.map((card) => {
        const optionKeys = optionMaps.get(card.key);
        return {
          ...card,
          key: cardKeys.get(card.key),
          options: card.options.map((option) => ({
            ...option,
            id: optionKeys.get(option.id),
          })),
          correct_option_ids: card.correct_option_ids.map((id) =>
            optionKeys.get(id),
          ),
          depends_on: card.depends_on.map((id) => cardKeys.get(id)),
        };
      }),
    }));
    pages = [...draft.revision.pages, ...pages];
  }
  validatePages(pages);
  return {
    ...draft,
    guide:
      mode === "replace"
        ? {
            ...draft.guide,
            ...(content.title !== undefined ? { title: content.title } : {}),
            ...(content.description !== undefined
              ? { description: content.description }
              : {}),
          }
        : draft.guide,
    revision: { ...draft.revision, pages,
      // Replace sets the course language and translations when the JSON has them; append only adds text.
      ...(mode === "replace" && content.content_locale ? { content_locale: content.content_locale } : {}),
      ...(translations ? { translations } : {}),
      ...(content.audience ? { policy: { ...draft.revision.policy, audience: content.audience.type, position_ids: content.audience.position_ids, user_ids: [] } } : {}),
    },
  };
}

export const SAMPLE_TRAINING_GUIDE_JSON = JSON.stringify(
  {
    schema: TRAINING_JSON_SCHEMA,
    content_locale: "nb",
    title: "Trygg oppstart",
    description: "En kort gjennomgang før arbeidet begynner.",
    pages: [
      {
        key: "intro",
        title: "Rutiner",
        cards: [
          {
            key: "read",
            type: "acknowledgement",
            title: "Kjenn rutinene",
            body: "<p>Les bedriftens rutiner og spør ansvarlig hvis noe er uklart.</p>",
            required: true,
          },
          {
            key: "question",
            type: "multiple_choice",
            title: "Hva gjør du ved en farlig situasjon?",
            body: "<p>Velg alle riktige svar.</p>",
            required: true,
            options: [
              { id: "stop", text: "Stopp farlig arbeid" },
              { id: "report", text: "Varsle ansvarlig" },
              { id: "ignore", text: "Fortsett uten å varsle" },
            ],
            correct_option_ids: ["stop", "report"],
            explanation: "Stopp arbeidet og varsle ansvarlig.",
            depends_on: ["read"],
          },
        ],
      },
    ],
    translations: {
      en: {
        title: "Safe start",
        description: "A short review before work begins.",
        pages: { intro: { title: "Procedures" } },
        cards: {
          read: { title: "Know the procedures", body: "<p>Read the company procedures and ask the person in charge if anything is unclear.</p>" },
          question: { title: "What do you do in a dangerous situation?", body: "<p>Select all correct answers.</p>", explanation: "Stop the work and notify the person in charge.", options: { stop: "Stop dangerous work", report: "Notify the person in charge", ignore: "Carry on without reporting" } },
        },
      },
      sv: {
        title: "Trygg start",
        description: "En kort genomgång innan arbetet börjar.",
        pages: { intro: { title: "Rutiner" } },
        cards: {
          read: { title: "Känn till rutinerna", body: "<p>Läs företagets rutiner och fråga ansvarig om något är oklart.</p>" },
          question: { title: "Vad gör du i en farlig situation?", body: "<p>Välj alla rätta svar.</p>", explanation: "Stoppa arbetet och meddela ansvarig.", options: { stop: "Stoppa farligt arbete", report: "Meddela ansvarig", ignore: "Fortsätt utan att meddela" } },
        },
      },
      pl: {
        title: "Bezpieczny start",
        description: "Krótkie omówienie przed rozpoczęciem pracy.",
        pages: { intro: { title: "Procedury" } },
        cards: {
          read: { title: "Poznaj procedury", body: "<p>Przeczytaj procedury firmy i zapytaj osobę odpowiedzialną, jeśli coś jest niejasne.</p>" },
          question: { title: "Co robisz w niebezpiecznej sytuacji?", body: "<p>Zaznacz wszystkie poprawne odpowiedzi.</p>", explanation: "Przerwij pracę i powiadom osobę odpowiedzialną.", options: { stop: "Przerwij niebezpieczną pracę", report: "Powiadom osobę odpowiedzialną", ignore: "Kontynuuj bez zgłaszania" } },
        },
      },
    },
  },
  null,
  2,
);
